import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import JourneyMap from "@/components/JourneyMap";
import { useAuth } from "@/context/AuthContext";
import { useEntries } from "@/context/EntriesContext";
import { SESSIONS, MILESTONES, WEEKS, WEEKLY_RHYTHM, CHECKIN_QUESTIONS, stageOf, isIntakeComplete } from "@/data/bridgeData";
import MicButton from "@/components/MicButton";
import { ArrowRight, Compass, Lock } from "lucide-react";

export default function Studio() {
  const { user } = useAuth();
  const { getEntry, saveEntry, loading } = useEntries();
  const navigate = useNavigate();

  const currentWeek = Number(getEntry("meta:currentWeek", 1)) || 1;
  const weekInfo = WEEKS.find((w) => w.week === currentWeek);
  const stage = weekInfo ? stageOf(weekInfo.stage) : stageOf("discover");
  const completedMs = MILESTONES.filter((m) => getEntry(`milestone:${m.n}:done`, false)).length;
  const nextSession = SESSIONS.find((s) => s.week >= currentWeek) || SESSIONS[SESSIONS.length - 1];
  const intakeDone = isIntakeComplete(getEntry);

  return (
    <Layout>
      {/* Hero */}
      <div className="mb-10">
        <div className="label-eyebrow mb-3">Your studio</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 leading-none">
          Welcome, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="mt-4 max-w-2xl text-stone-600 leading-relaxed">
          A quiet place for the deep work between sessions – reflect, synthesize with your assistant,
          then test in the real world. Move at the pace the work asks for.
        </p>
      </div>

      {!loading && !intakeDone && (
        <div data-testid="studio-intake-banner" className="mb-10 flex flex-wrap items-center justify-between gap-4 rounded-sm border border-brand-accent/40 bg-brand-parchment/50 p-6">
          <div className="flex items-start gap-3">
            <Lock size={20} className="mt-0.5 text-brand-primary shrink-0" />
            <div>
              <div className="font-serif text-2xl text-stone-900">Start with your intake</div>
              <p className="text-sm text-stone-600 max-w-xl">Before Session 1 unlocks, record where you're starting from, what you're moving toward, and an honest baseline. It frames the whole journey.</p>
            </div>
          </div>
          <button
            data-testid="studio-go-intake"
            onClick={() => navigate("/intake")}
            className="group inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover"
          >
            Begin intake <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      )}

      {/* Status row */}
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <div className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="label-eyebrow mb-2">Current week</div>
          <div className="flex items-end gap-3">
            <span className="font-serif text-5xl text-stone-900">{currentWeek}</span>
            <span className="mb-1.5 text-sm text-stone-500">of 16</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <select
              data-testid="week-selector"
              value={currentWeek}
              onChange={(e) => saveEntry("meta:currentWeek", Number(e.target.value), true)}
              className="text-sm bg-transparent border border-stone-200 rounded-sm px-2 py-1 text-stone-600 focus:outline-none focus:border-brand-primary"
            >
              {WEEKS.map((w) => <option key={w.week} value={w.week}>Week {w.week} – {w.focus}</option>)}
            </select>
          </div>
        </div>
        <div className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="label-eyebrow mb-2">Current stage</div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: stage.color }} />
            <span className="font-serif text-3xl text-stone-900">{stage.name}</span>
          </div>
          <p className="mt-2 text-sm text-stone-500 leading-snug">{stage.goal}</p>
        </div>
        <div className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="label-eyebrow mb-2">Milestones</div>
          <div className="flex items-end gap-3">
            <span className="font-serif text-5xl text-stone-900">{completedMs}</span>
            <span className="mb-1.5 text-sm text-stone-500">of 6 reached</span>
          </div>
          <button data-testid="goto-milestones" onClick={() => navigate("/milestones")} className="mt-3 inline-flex items-center gap-1 text-sm text-brand-primary hover:gap-2 transition-all">
            View milestones <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <JourneyMap />

      {/* Next session + weekly rhythm */}
      <div className="grid lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 rounded-sm border border-stone-200 bg-white p-8">
          <div className="label-eyebrow mb-3">Up next</div>
          <h3 className="font-serif text-3xl text-stone-900">Session {nextSession.id} – {nextSession.title}</h3>
          <p className="mt-3 text-stone-600 leading-relaxed">{nextSession.purpose}</p>
          <button
            data-testid="open-next-session"
            onClick={() => navigate(intakeDone ? `/sessions/${nextSession.id}` : "/intake")}
            className="group mt-6 inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover"
          >
            {intakeDone ? "Open session workspace" : "Complete intake first"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>

        <div className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-8">
          <div className="flex items-center gap-2 mb-4">
            <Compass size={16} strokeWidth={1.6} className="text-brand-primary" />
            <div className="label-eyebrow">Weekly rhythm</div>
          </div>
          <ul className="space-y-3">
            {WEEKLY_RHYTHM.map((r) => (
              <li key={r.when}>
                <div className="text-sm font-medium text-stone-900">{r.when}</div>
                <div className="text-sm text-stone-500">{r.what}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* End-of-week check-in */}
      <div className="mt-10 rounded-sm border border-stone-200 bg-white p-8">
        <div className="label-eyebrow mb-3">End-of-week check-in</div>
        <h3 className="font-serif text-3xl text-stone-900 mb-5">A short learning note</h3>
        <div className="space-y-5">
          {CHECKIN_QUESTIONS.map((q, i) => (
            <div key={q}>
              <label className="text-base text-stone-700">{q}</label>
              <textarea
                data-testid={`checkin-${i}`}
                value={getEntry(`checkin:week${currentWeek}:${i}`, "")}
                onChange={(e) => saveEntry(`checkin:week${currentWeek}:${i}`, e.target.value)}
                rows={2}
                className="mt-1 w-full resize-y bg-transparent border-b border-stone-300 py-1.5 text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
                placeholder="A sentence or two…"
              />
              <div className="mt-1 flex justify-end">
                <MicButton getValue={() => getEntry(`checkin:week${currentWeek}:${i}`, "")} onChange={(v) => saveEntry(`checkin:week${currentWeek}:${i}`, v)} testid={`checkin-${i}-mic`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
